import React, { useState } from "react";

/**
 * CuvrIQ – Premium Conversion Beta Page
 */

const BRAND_PRIMARY = "#0F172A";
const BRAND_ACCENT = "#06B6D4";
const TEXT = "#0B1220";
const MUTED = "#6B7280";
const BORDER = "#E5E7EB";

const Icon = ({ path, size = 18, color = "currentColor" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    style={{ flex: "0 0 auto" }}
  >
    <path d={path} />
  </svg>
);

const icons = {
  arrowRight: "M5 12h14M12 5l7 7-7 7",
  external:
    "M18 13v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3",
};

export default function CuvrIQBetaOverview() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    practice: "",
    email: "",
    agree: false,
  });

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!form.name || !form.email || !form.practice) {
      setError("Please enter your name, email, and practice.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/beta-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");

      setSuccess(true);
      setForm({ name: "", practice: "", email: "", agree: false });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page">
      <style>{`
        :root {
          --brand-primary: ${BRAND_PRIMARY};
          --brand-accent: ${BRAND_ACCENT};
          --muted: ${MUTED};
          --border: ${BORDER};
        }

        * { box-sizing: border-box; }
        html, body { margin: 0; overflow-x: hidden; }

        .page {
          font-family: system-ui, -apple-system, sans-serif;
          color: ${TEXT};
        }

        .container {
          max-width: 820px;
          margin: 0 auto;
          padding: 8px 20px 120px;
        }

        .top-logo {
          text-align: center;
          margin-bottom: 8px;
          margin-top: 3px;
        }

        .top-logo img {
          width: 160px;
          height: auto;
        }

        .kicker {
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: .12em;
          color: var(--brand-accent);
          font-weight: 700;
        }

        .hero-title {
          font-size: 28px;
          font-weight: 900;
          line-height: 1.1;
          margin-top: 14px;
          color: var(--brand-primary);
        }

        .hero-sub {
          margin-top: 20px;
          font-size: 18px;
          color: var(--muted);
          line-height: 1.6;
        }

        .hero-ctas {
          margin-top: 28px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .btn {
          padding: 15px 22px;
          border-radius: 16px;
          font-weight: 700;
          text-align: center;
          text-decoration: none;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          transition: all .2s ease;
        }

        .btn-primary {
          background: var(--brand-primary);
          color: white;
        }

        .btn-primary:hover {
          background: var(--brand-accent);
        }

        .btn-outline {
          border: 1px solid var(--border);
          color: var(--brand-primary);
        }

        .btn-outline:hover {
          background: #f3f4f6;
        }

        .trust-line {
          margin-top: 20px;
          font-weight: 600;
          font-size: 14px;
        }

        .value-bullets {
          margin-top: 20px;
          font-size: 14px;
          line-height: 1.7;
          color: #374151;
        }

        .section {
          margin-top: 70px;
        }

        .section h2 {
          font-size: 28px;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        input {
          padding: 14px;
          border-radius: 12px;
          border: 1px solid var(--border);
          font-size: 15px;
        }

        .checkbox {
          font-size: 14px;
        }

        .alert {
          padding: 12px;
          border-radius: 10px;
          font-size: 14px;
        }

        .alert.error {
          background: #fee2e2;
          color: #991b1b;
        }

        .alert.ok {
          background: #dcfce7;
          color: #166534;
        }

        /* Sticky CTA (Logo Removed) */

        .sticky-mobile-cta {
          display: none;
        }

        @media (max-width: 900px) {
          .sticky-mobile-cta {
            display: flex;
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            padding: 16px 20px;
            justify-content: center;
            background: rgba(15, 23, 42, 0.96);
            backdrop-filter: blur(10px);
            border-top: 1px solid rgba(255,255,255,0.08);
            box-shadow: 0 -10px 30px rgba(0,0,0,0.3);
            z-index: 999;
          }

          .sticky-btn {
            background: white;
            color: var(--brand-primary);
            font-weight: 700;
            padding: 12px 22px;
            border-radius: 999px;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 15px;
          }

          .sticky-btn:hover {
            background: var(--brand-accent);
            color: white;
          }

          body {
            padding-bottom: 100px;
          }
        }
      `}</style>

      <div className="container">
        <div className="top-logo">
          <img src="/cuvriq-logo-1.png" alt="CuvrIQ logo" />
        </div>

        <p className="kicker">Invite-only beta</p>

        <h1 className="hero-title">
          Stop calling insurance.
          <br />
          Get instant coverage answers instead.
        </h1>

        <p className="hero-sub">
          Most solo practices waste hours every week re-verifying plans that other offices have already verified.
          <br /><br />
          <strong>CuvrIQ lets you upload once and reuse forever.</strong>
        </p>

        <div className="hero-ctas">
          <a href="#apply" className="btn btn-primary">
            🚀 Join the Beta <Icon path={icons.arrowRight} size={18} />
          </a>

          <a href="/cuvriq-video" className="btn btn-outline">
            See CuvrIQ in action <Icon path={icons.external} size={18} />
          </a>
        </div>

        <div className="trust-line">
          Built by a practicing dentist for solo & small-group practices.
        </div>

        <div className="value-bullets">
          • Instant, plan-backed coverage answers<br/>
          • Zero PHI storage model<br/>
          • Designed specifically for small practices<br/>
          • No integrations required
        </div>

        <div className="section" id="apply">
          <h2>Request Your Beta Invite</h2>

          <form onSubmit={submit} className="form">
            <input
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Practice name"
              value={form.practice}
              onChange={(e) => setForm({ ...form, practice: e.target.value })}
            />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />

            <div className="checkbox">
              <input
                type="checkbox"
                checked={form.agree}
                onChange={(e) => setForm({ ...form, agree: e.target.checked })}
              /> I agree to be contacted about the beta.
            </div>

            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert ok">You're in. We'll email you shortly.</div>}

            <button type="submit" className="btn btn-primary">
              {loading ? "Submitting…" : "Submit Request"}
            </button>
          </form>
        </div>
      </div>

      <div className="sticky-mobile-cta">
        <a href="#apply" className="sticky-btn">
          🚀 Join the Beta <Icon path={icons.arrowRight} size={16} />
        </a>
      </div>
    </div>
  );
}
